import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { DownloadIcon } from '~components/icons/DownloadIcon';
import { queries } from '~modules/media-library/controller/api/Queries';
import { type AssetId } from '~modules/media-library/model/Asset';
import { type DownloadFormat } from '~modules/media-library/model/DownloadFormat';
import { Button } from '~modules/media-library/view/Button';
import { ShareAction } from '~modules/media-library/view/ShareModal/ShareAction';
import { ShareFormatListItemPanel } from '~modules/media-library/view/ShareModal/ShareFormatListItemPanel';
import { makeProgramaticallyLinkClick } from '~utils/link';

type Props = {
  assetId: AssetId;
  downloadFormat: DownloadFormat;
  shareAction: ShareAction;
};

export function ShareFormatListItemPanelController({
  assetId,
  downloadFormat,
  shareAction = ShareAction.Download,
}: Props) {
  const [format, setFormat] = useState<DownloadFormat>(downloadFormat);

  // Handler: onDownload fetches download url and gives it back
  const onDownload = useCallback((href: string) => makeProgramaticallyLinkClick({ href }), []);

  // load details for download format (filename and downloadUrl)
  const { data: downloadFormats } = useQuery({
    ...queries.mediaLibrary.downloadFormat(assetId, downloadFormat.key),
    enabled: Boolean(assetId) && Boolean(downloadFormat.key) && !Boolean(downloadFormat.downloadUrl),
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (downloadFormats === undefined) return;
    setFormat(downloadFormats?.[downloadFormat.key]);
  }, [downloadFormat, downloadFormats]);

  return (
    <ShareFormatListItemPanel downloadFormat={format} isLoading={!Boolean(format.downloadUrl)}>
      {shareAction === ShareAction.Download && format?.downloadAllowed ? (
        <Button variant="icon" className="ml-icon-download" onClick={() => onDownload(format.downloadUrl)}>
          <DownloadIcon />
        </Button>
      ) : null}
    </ShareFormatListItemPanel>
  );
}
